
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { Loader2, Info, AlertTriangle } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { useAuthSession } from '@/hooks/use-auth-session';
import { useToast } from '@/hooks/use-toast';
import { TripsTabs } from '@/components/trips/TripsTabs';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

const Trips = () => {
  const [searchParams] = useSearchParams();
  const defaultTab = searchParams.get('tab') || 'destinations';
  const session = useAuthSession();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: favorites, isLoading: isFavoritesLoading } = useQuery({
    queryKey: ['favorites', session?.user?.id],
    queryFn: async () => {
      if (!session?.user?.id) {
        console.log('No user session, returning empty favorites');
        return [];
      }
      
      console.log('Fetching favorites for user:', session.user.id);
      
      const { data, error } = await supabase
        .from('favorites')
        .select(`
          *,
          destination:destinations(*),
          host:profiles(
            id,
            username,
            avatar_url,
            bio,
            location,
            hosting_style,
            languages
          )
        `)
        .eq('user_id', session.user.id);
      
      if (error) {
        console.error('Error fetching favorites:', error);
        throw error;
      }
      
      console.log('Fetched favorites:', data);
      return data || [];
    },
    enabled: !!session?.user?.id,
  });

  const { data: sessions, isLoading: isSessionsLoading } = useQuery({
    queryKey: ['sessions', session?.user?.id],
    queryFn: async () => {
      if (!session?.user?.id) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase
        .from('session_participants')
        .select(`
          *,
          session:host_availability(
            id,
            start_time,
            end_time,
            title,
            host:profiles(
              id,
              username,
              avatar_url
            )
          )
        `)
        .eq('participant_id', session.user.id);
      
      if (error) throw error;
      return data;
    },
    enabled: !!session?.user?.id,
    meta: {
      onError: () => {
        toast({
          title: "Error loading sessions",
          description: "Please try again later",
          variant: "destructive",
        });
      }
    }
  });

  const cancelSessionMutation = useMutation({
    mutationFn: async (sessionId: string) => {
      const now = new Date();
      const sessionItem = sessions?.find(s => s.id === sessionId);
      
      if (!sessionItem) {
        throw new Error('Session not found');
      }

      const sessionStartTime = new Date(sessionItem.session.start_time);
      const hoursDifference = (sessionStartTime.getTime() - now.getTime()) / (1000 * 60 * 60);

      if (hoursDifference < 72) {
        throw new Error('Sessions can only be cancelled at least 72 hours before the start time');
      }

      console.log("Invoking send-cancellation-email with:", {
        sessionId: sessionItem.session_id,
        cancelledBy: 'participant',
        cancellerUserId: session?.user?.id
      });

      const { data: emailData, error: emailError } = await supabase.functions.invoke('send-cancellation-email', {
        body: {
          sessionId: sessionItem.session_id,
          cancelledBy: 'participant',
          cancellerUserId: session?.user?.id
        }
      });

      if (emailError) {
        console.error('Error sending cancellation email:', emailError);
        // We log but don't throw here to avoid failing the cancellation if only the email fails
      } else {
        console.log("Cancellation email response:", emailData);
      }

      const { error } = await supabase
        .from('session_participants')
        .delete()
        .eq('id', sessionId);

      if (error) throw error;

      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sessions'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] }); // Refresh notifications
      toast({
        title: "Session cancelled",
        description: "Your session has been successfully cancelled and notification emails have been sent.",
      });
    },
    onError: (error: Error) => {
      toast({
        variant: 'destructive',
        title: "Error cancelling session",
        description: error.message,
      });
    },
  });

  if (isFavoritesLoading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  const handleCancelSession = (sessionId: string) => {
    if (window.confirm('Are you sure you want to cancel this session? This will also remove the Google Calendar event if one was created.')) {
      cancelSessionMutation.mutate(sessionId);
    }
  };

  const handleJoinMeeting = (meetingLink: string | null, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (meetingLink) {
      window.open(meetingLink, '_blank', 'noopener,noreferrer');
    } else {
      toast({
        variant: 'destructive',
        title: "Meeting link unavailable",
        description: "The meeting link for this session is not available.",
      });
    }
  };

  const hasFallbackSessions = Boolean(sessions?.some(s => s.fallback));
  const hasMeetUnavailableSessions = Boolean(sessions?.some(s => s.meet_unavailable));
  const hasOAuthClientNotFoundError = Boolean(sessions?.some(s => 
    s.error_details?.message?.includes("The OAuth client was not found")
  ));

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-8">My Trips</h1>
      
      {hasFallbackSessions && (
        <Alert variant={hasOAuthClientNotFoundError ? "destructive" : "info"} className="mb-6">
          {hasOAuthClientNotFoundError ? (
            <AlertTriangle className="h-4 w-4" />
          ) : (
            <Info className="h-4 w-4" />
          )}
          <AlertTitle>
            {hasOAuthClientNotFoundError 
              ? "Google OAuth Configuration Error" 
              : "About Google Meet Links"}
          </AlertTitle>
          <AlertDescription>
            {hasOAuthClientNotFoundError ? (
              <>
                <p className="font-bold">Error: "The OAuth client was not found"</p>
                <p className="mt-2">
                  This error typically means your Google OAuth client ID is incorrect or the OAuth 
                  client has been deleted from Google Cloud Console. Please check:
                </p>
                <ul className="list-disc pl-6 mt-2">
                  <li>Verify the GOOGLE_OAUTH_CLIENT_ID is correct in your Supabase Edge Function secrets</li>
                  <li>Ensure the OAuth client exists in your Google Cloud Console</li>
                  <li>Make sure the project and client ID referenced in your OAuth credentials still exist</li>
                  <li>Confirm the client type is set to "Web application" in Google Cloud Console</li>
                </ul>
                <p className="mt-2">
                  Until this is fixed, temporary Google Meet links will be used, but they may not work correctly.
                </p>
              </>
            ) : hasMeetUnavailableSessions ? (
              <>
                Some of your sessions are using temporary Google Meet links because Google Meet 
                integration is not properly configured. This typically happens when:
                <ul className="list-disc pl-6 mt-2">
                  <li>The Google OAuth credentials are invalid or missing</li>
                  <li>Google Meet is not enabled for the account</li>
                  <li>The Google account does not have the required Calendar permissions</li>
                </ul>
                <p className="mt-2">
                  Make sure your Google account has Google Meet enabled and that you have granted the 
                  appropriate Calendar and Meet permissions when creating the OAuth credentials.
                </p>
              </>
            ) : (
              "Some of your sessions are using temporary Google Meet links because Google Meet integration is not fully configured. These links will still work but may require participants to have Google Workspace accounts."
            )}
          </AlertDescription>
        </Alert>
      )}
      
      <TripsTabs
        defaultTab={defaultTab}
        favorites={favorites || []}
        sessions={sessions || []}
        onCancelSession={handleCancelSession}
        onJoinMeeting={handleJoinMeeting}
      />
    </div>
  );
};

export default Trips;
